import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

// Home rails hold dozens of preview clips (the loop clone doubles them), so
// clips are managed centrally instead of one observer + one download each:
//  - a clip's file is only requested once its card is within ~1 card of the
//    viewport, so nothing offscreen costs bandwidth up front;
//  - it plays only while at least half visible, and only after a short delay,
//    so a fast scroll doesn't start (and instantly stop) clips it flies past;
//  - at most MAX_PLAYING clips decode at once — the oldest is paused first.
const MAX_PLAYING = 10;
const PLAY_DELAY_MS = 150;
const LOAD_MARGIN = '0px 320px';

interface Handlers {
  load: () => void;
  play: () => void;
  stop: () => void;
}

const handlers = new WeakMap<Element, Handlers>();
const timers = new WeakMap<Element, number>();
const playing = new Set<HTMLVideoElement>();

let loadObserver: IntersectionObserver | undefined;
let playObserver: IntersectionObserver | undefined;

function observers() {
  loadObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        handlers.get(entry.target)?.load();
        loadObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: LOAD_MARGIN }
  );

  playObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const target = entry.target;
        window.clearTimeout(timers.get(target));
        if (entry.isIntersecting) {
          timers.set(
            target,
            window.setTimeout(() => handlers.get(target)?.play(), PLAY_DELAY_MS)
          );
        } else {
          handlers.get(target)?.stop();
        }
      }
    },
    { threshold: 0.5 }
  );

  return { loadObserver, playObserver };
}

/**
 * Wires a looping preview <video> into the shared loader/player above.
 * Returns `loaded` (render the `src` only once true) and `onLoadedData` (pass
 * it to the video so a clip whose data arrives after it became visible still
 * starts).
 */
export function useLazyLoopVideo(ref: RefObject<HTMLVideoElement | null>, enabled: boolean) {
  const [loaded, setLoaded] = useState(false);
  const wanted = useRef(false);

  const start = useCallback((video: HTMLVideoElement) => {
    if (playing.size >= MAX_PLAYING) {
      const oldest = playing.values().next().value;
      if (oldest) {
        oldest.pause();
        playing.delete(oldest);
      }
    }
    playing.add(video);
    void video.play().catch(() => playing.delete(video));
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!enabled || !video) return;

    const { loadObserver, playObserver } = observers();
    handlers.set(video, {
      load: () => setLoaded(true),
      play: () => {
        wanted.current = true;
        if (video.readyState >= 2) start(video);
      },
      stop: () => {
        wanted.current = false;
        video.pause();
        playing.delete(video);
      },
    });
    loadObserver.observe(video);
    playObserver.observe(video);

    return () => {
      loadObserver.unobserve(video);
      playObserver.unobserve(video);
      window.clearTimeout(timers.get(video));
      playing.delete(video);
      handlers.delete(video);
    };
  }, [enabled, ref, start]);

  const onLoadedData = useCallback(() => {
    const video = ref.current;
    if (video && wanted.current) start(video);
  }, [ref, start]);

  return { loaded, onLoadedData };
}
